import { History, AlertOctagon, User, Bot, UserCheck, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const ESCALATION_LABELS = { ai_uncertain: "AI couldn't confidently answer" };
const FEEDBACK_STYLES = {
  answered: { className: "border-emerald-200 text-emerald-600", label: "Question Answered" },
  need_more_help: { className: "border-amber-200 text-amber-600", label: "Needed More Help" },
  request_human: { className: "border-blue-200 text-blue-600", label: "Requested Human" },
};

export const ThreadHistoryDialog = ({ open, onOpenChange, selectedThread, threadHistory, loadingHistory, onResolve }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-500" />
                Conversation History
              </DialogTitle>
              <DialogDescription>
                Thread ID: {selectedThread?.slice(0, 20)}...
              </DialogDescription>
            </DialogHeader>
            
            {loadingHistory && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            )}
            {!loadingHistory && !threadHistory && (
              <p className="text-center text-slate-500 py-8">No conversation history found</p>
            )}
            {!loadingHistory && threadHistory && (
              <div className="space-y-4">
                {/* Escalation Info */}
                {threadHistory.escalation && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertOctagon className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Escalated: {ESCALATION_LABELS[threadHistory.escalation.reason] || "Customer requested representative"}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      {new Date(threadHistory.escalation.escalated_at).toLocaleString()}
                    </p>
                  </div>
                )}
                
                {/* Conversation Timeline */}
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3 pr-4">
                    {threadHistory.conversation.map((msg) => (
                      <div 
                        key={msg.log_id || `${msg.type}-${msg.timestamp}`}
                        className={`p-3 rounded-lg ${msg.type === "customer" ? "bg-slate-100 ml-0 mr-8" : "bg-violet-50 ml-8 mr-0"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {msg.type === "customer" ? (
                            <User className="w-3 h-3 text-slate-500" />
                          ) : (
                            <Bot className="w-3 h-3 text-violet-500" />
                          )}
                          <span className="text-xs font-medium text-slate-600">
                            {msg.type === "customer" ? "Customer" : "AI Assistant"}
                          </span>
                          {msg.escalated && (
                            <Badge variant="outline" className="text-[10px] border-red-200 text-red-600">
                              Escalated
                            </Badge>
                          )}
                          <span className="text-xs text-slate-400 ml-auto">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                {/* Feedback Info */}
                {threadHistory.feedback && threadHistory.feedback.length > 0 && (
                  <div className="pt-3 border-t border-slate-200">
                    <p className="text-xs font-medium text-slate-500 mb-2">Customer Feedback</p>
                    {threadHistory.feedback.map((fb) => (
                      <Badge 
                        key={fb.feedback_id || fb.created_at}
                        variant="outline" 
                        className={`mr-2 ${(FEEDBACK_STYLES[fb.response_type] || FEEDBACK_STYLES.request_human).className}`}
                      >
                        {(FEEDBACK_STYLES[fb.response_type] || FEEDBACK_STYLES.request_human).label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {threadHistory?.is_escalated && (
                <Button 
                  onClick={() => onResolve(selectedThread)}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <UserCheck className="w-4 h-4" />
                  Mark Resolved
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
  </Dialog>
);
